import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { SlLEContext } from '../../global-constants';
import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import {
	IMaterialStatus, IMaterialInfo, CompletionStatus, MaterialType, MaterialRequirementType,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import {
	Service, Container, StreakService, AuthService, IStreakDTO, StreakSource,
} from '../../../../symphony';

@Service()
export class SlStreakService {
	private api = Container.take(SlLEContext, SlApiService);

	private data = Container.take(SlLEContext, SlDataService);

	private streakService = Container.take('global', StreakService);

	private authService = Container.take('global', AuthService);

	public setStreakGoal = (goalDays: number): Observable<null> => this.api.setStreakGoal(this.authService.getUser().id, goalDays);

	public tryAchieveStreak = (
		statusChanges: IMaterialStatus[],
		solvedMaterial?: IMaterialInfo,
	): Observable<IStreakDTO> => {
		const lesson = this.data.lesson$.value?.materialInfo
			|| this.data.lessonBackup$.value?.materialInfo;
		const lessonCompleted = statusChanges.some(
			(status) => (status.completion === CompletionStatus.Completed
				|| status.completion === CompletionStatus.ByPassed)
				&& status.materialRelationId === lesson?.header?.materialRelationId,
		);

		const streakSource = lessonCompleted
			? this.getLessonStreakSource(lesson?.header?.typeId)
			: this.getMaterialStreakSource(solvedMaterial);

		if (streakSource) {
			return this.getPredictedStreaks(
				this.authService.getUser().id,
				streakSource,
			);
		}

		return of(null);
	};

	private getLessonStreakSource = (typeId: MaterialType): StreakSource => {
		switch (typeId) {
			case MaterialType.Lesson:
				return StreakSource.Lesson;
			case MaterialType.ModuleQuiz:
				return StreakSource.ModuleQuiz;
			case MaterialType.Booster:
				return StreakSource.Booster;
			default:
				return null;
		}
	};

	private getMaterialStreakSource = (
		material: IMaterialInfo,
	): StreakSource => {
		if (material?.header?.typeId === MaterialType.CodeProject) {
			return StreakSource.CodeProject;
		}
		if (
			material?.header?.typeId === MaterialType.CodeCoach
			&& material?.requirementTypeId === MaterialRequirementType.Optional
		) {
			return StreakSource.OptionalCC;
		}
		return null;
	};

	private getPredictedStreaks = (
		userId: number,
		source: StreakSource,
	): Observable<IStreakDTO> => this.api.getPredictedStreaks(userId).pipe(
		tap((streak) => this.streakService.tryUpdateStreak(streak, source)),
		catchError(() => of(null)),
	);
}
